import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TourStep } from '../types/TourTypes';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Target } from 'lucide-react';

interface TourStepOverlayProps {
  step: TourStep;
  onHide: () => void;
  onInteraction?: (stepId: string) => void;
}

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const TourStepOverlay = ({ step, onHide, onInteraction }: TourStepOverlayProps) => {
  const { i18n } = useTranslation();
  const [elementPosition, setElementPosition] = useState<ElementPosition | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; arrowPosition: 'top' | 'bottom'; arrowLeft: string } | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const observerRef = useRef<MutationObserver | null>(null);
  const currentLang = i18n.language as 'pt' | 'en';

  const getStepName = () => {
    return step.name[currentLang] || step.name['pt'];
  };

  const getStepDescription = () => {
    return step.description[currentLang] || step.description['pt'];
  };

  const getStepObjective = () => {
    return step.objective[currentLang] || step.objective['pt'];
  };

  const handleElementClick = useCallback(() => {
    console.log('[TourStepOverlay] Element clicked! Marking as interacted:', step.id);
    // Marca como interagido
    if (onInteraction) {
      onInteraction(step.id);
    }
    // Esconde o overlay imediatamente quando o elemento é clicado
    onHide();
  }, [onHide, onInteraction, step.id]);

  const updatePosition = () => {
    const targetElement = document.querySelector(step.targetElement);
    
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setElementPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      });

      // Calcula posição do popover - detecta se deve ficar acima ou abaixo
      const popoverWidth = 400; // Largura estimada do popover
      const popoverHeight = 200; // Altura estimada do popover
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Verifica espaço disponível acima e abaixo
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let popoverTop: number;
      let arrowPosition: 'top' | 'bottom';
      
      // Se há mais espaço acima OU se não há espaço suficiente embaixo, posiciona acima
      if (spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
        popoverTop = rect.top + window.scrollY - popoverHeight - 32; // Aumentado de 16 para 32
        arrowPosition = 'top'; // Arrow na parte inferior do card
      } else {
        popoverTop = rect.bottom + window.scrollY + 16;
        arrowPosition = 'bottom'; // Arrow na parte superior do card
      }
      
      let popoverLeft = rect.left + window.scrollX;
      
      // Ajusta se o popover sair da tela pela direita
      if (popoverLeft + popoverWidth > viewportWidth) {
        // Alinha o popover à direita do elemento
        popoverLeft = rect.right + window.scrollX - popoverWidth;
        
        // Garante que não saia pela esquerda
        if (popoverLeft < 0) {
          popoverLeft = 10; // 10px de margem
        }
      }

      // Calcula posição da arrow para apontar para o centro do elemento
      const elementCenterX = rect.left + window.scrollX + (rect.width / 2);
      const arrowLeftPosition = elementCenterX - popoverLeft;
      
      // Ajusta para centralizar a arrow (considerando sua largura de 12px)
      const arrowWidth = 12;
      const arrowLeftAdjusted = arrowLeftPosition - (arrowWidth / 2);
      
      // Garante que a arrow não saia do popover
      const minArrowPos = 20; // margem mínima
      const maxArrowPos = popoverWidth - 20; // margem máxima
      const clampedArrowPos = Math.max(minArrowPos, Math.min(maxArrowPos, arrowLeftAdjusted));

      setPopoverPosition({
        top: popoverTop,
        left: popoverLeft,
        arrowPosition: arrowPosition,
        arrowLeft: `${clampedArrowPos}px` // Posição em pixels para melhor precisão
      });
    } else {
      setElementPosition(null);
      setPopoverPosition(null);
    }
  };

  useEffect(() => {
    // Reseta visibilidade e animação ao trocar de step
    setIsVisible(false);
    setIsAnimating(true);

    // Aguarda o DOM atualizar
    const timer = setTimeout(() => {
      updatePosition();
    }, 100);

    // Ativa o fade-in e inicia a animação de slide
    const fadeTimer = setTimeout(() => {
      setIsVisible(true);
    }, 150);

    // Finaliza animação de slide após duração
    const animTimer = setTimeout(() => {
      setIsAnimating(false);
    }, 800);

    // Observa mudanças no DOM
    observerRef.current = new MutationObserver(() => {
      updatePosition();
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Atualiza posição no scroll e resize
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    // Usa event delegation para capturar cliques em TODOS os elementos (mesmo os renderizados dinamicamente)
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as Element;
      // Verifica se o elemento clicado ou algum ancestral corresponde ao seletor
      if (target.matches(step.targetElement) || target.closest(step.targetElement)) {
        handleElementClick();
      }
    };

    document.addEventListener('click', handleDocumentClick, true); // capture phase

    return () => {
      clearTimeout(timer);
      clearTimeout(fadeTimer);
      clearTimeout(animTimer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      document.removeEventListener('click', handleDocumentClick, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.targetElement, handleElementClick]);

  if (!elementPosition || !popoverPosition) {
    return null;
  }

  const padding = 4;

  return (
    <>
      {/* Overlay dividido em 4 partes para criar o "buraco" clicável */}
      {/* Top */}
      <div
        className="tour-overlay"
        style={{
          top: 0,
          left: 0,
          width: '100%',
          height: `${elementPosition.top - padding}px`,
          position: 'fixed',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s ease-out'
        }}
      />
      
      {/* Left */}
      <div
        className="tour-overlay"
        style={{
          top: `${elementPosition.top - padding}px`,
          left: 0,
          width: `${elementPosition.left - padding}px`,
          height: `${elementPosition.height + padding * 2}px`,
          position: 'fixed',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s ease-out'
        }}
      />
      
      {/* Right */}
      <div
        className="tour-overlay"
        style={{
          top: `${elementPosition.top - padding}px`,
          left: `${elementPosition.left + elementPosition.width + padding}px`,
          width: `calc(100% - ${elementPosition.left + elementPosition.width + padding}px)`,
          height: `${elementPosition.height + padding * 2}px`,
          position: 'fixed',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s ease-out'
        }}
      />
      
      {/* Bottom */}
      <div
        className="tour-overlay"
        style={{
          top: `${elementPosition.top + elementPosition.height + padding}px`,
          left: 0,
          width: '100%',
          height: `calc(100% - ${elementPosition.top + elementPosition.height + padding}px)`,
          position: 'fixed',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
          pointerEvents: 'auto',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s ease-out'
        }}
      />

      {/* Spotlight border no elemento (apenas visual, sem bloquear cliques) */}
      <div
        className="tour-spotlight tour-spotlight-pulse"
        style={{
          top: `${elementPosition.top - padding}px`,
          left: `${elementPosition.left - padding}px`,
          width: `${elementPosition.width + padding * 2}px`,
          height: `${elementPosition.height + padding * 2}px`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      />

      {/* Popover informativo */}
      <Card
        className="tour-popover"
        style={{
          // Posição inicial: centro da tela, transição para posição final
          top: isAnimating 
            ? `${window.innerHeight / 2 - 100}px`
            : `${popoverPosition.top}px`,
          left: isAnimating 
            ? `${window.innerWidth / 2 - 200}px`
            : `${popoverPosition.left}px`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible 
            ? 'scale(1) rotateX(0deg)' 
            : 'scale(0.5) rotateX(-15deg)',
          transition: isAnimating
            ? 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
            : 'all 0.3s ease-out',
          boxShadow: isVisible 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 4px hsla(var(--primary), 0.4)' 
            : 'none',
          filter: isVisible ? 'blur(0px)' : 'blur(10px)',
          transformOrigin: 'center center',
          perspective: '1000px'
        }}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start gap-2">
            <Target 
              className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" 
              style={{
                animation: isVisible 
                  ? 'tour-icon-bounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55), tour-shake 0.5s ease-in-out 1s 2' 
                  : 'none'
              }}
            />
            <div className="flex-1">
              <CardTitle className="text-base">{getStepName()}</CardTitle>
              <CardDescription className="text-sm mt-1">
                {getStepDescription()}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="rounded-md bg-primary/10 p-3">
            <p className="text-xs font-medium text-primary mb-1">
              Objetivo:
            </p>
            <p className="text-sm">{getStepObjective()}</p>
          </div>
        </CardContent>
        <div 
          className={`tour-popover-arrow ${popoverPosition.arrowPosition}`}
          style={{ left: popoverPosition.arrowLeft }}
        />
      </Card>
    </>
  );
};
